import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, XCircle, CheckCircle2, Users, Scale, Download, FileText } from "lucide-react";
import ComparisonCard from "@/components/ComparisonCard";

function Section({ eyebrow, title, italic, children }) {
  return (
    <section className="py-16 md:py-24 border-b mv-hairline">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>{eyebrow}</span></div>
        <h2 className="font-display text-3xl md:text-5xl mv-green-text mt-3 leading-[1.05]">
          {title} {italic && <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>{italic}</span>}
        </h2>
        <div className="mt-8">{children}</div>
      </div>
    </section>
  );
}

const NEVER_RULES = [
  "Ask for your visa, residence, or immigration status abroad",
  "Use your property as security for other loans",
  "Change terms without your written agreement",
  "Resell your land while you are paying fairly",
];

export default function SafetyPage() {
  const [guardians, setGuardians] = useState([]);
  const [panel, setPanel] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [g, p] = await Promise.all([
          api.get("/verifiers?role=title-guardian"),
          api.get("/verifiers?role=review-panel"),
        ]);
        setGuardians(g.data.verifiers || []);
        setPanel(p.data.verifiers || []);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <main data-testid="safety-page">
      {/* HERO */}
      <section className="mv-green-bg text-white py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }}/>
        <div className="max-w-6xl mx-auto px-4 md:px-8 relative">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--mv-gold)" }}>§ Core Protections</div>
          <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[0.98] max-w-4xl">
            Forever Yours. <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Full stop.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-white/85 text-[15.5px] leading-relaxed">
            Your land. Your name. Forever. Once registered, we can never take it back — no fine print,
            no reversion clauses, no shared-ownership tricks. We do not own your home. You do.
          </p>
        </div>
      </section>

      {/* FOREVER YOURS PILLARS */}
      <Section eyebrow="§ Forever Yours · Title Promise" title="Three promises we sign" italic="in writing.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { i: <ShieldCheck size={22}/>, t: "Your land, your name, forever", d: "Title deed registered directly in your name — never in ours." },
            { i: <FileText size={22}/>, t: "No fine print, no traps", d: "Zero reversion clauses. Zero shared-ownership tricks. Zero hidden fees." },
            { i: <CheckCircle2 size={22}/>, t: "We do not own your home", d: "We open the door. You keep the key. Every step is documented." },
          ].map((c) => (
            <div key={c.t} className="mv-card p-6 h-full">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4 mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>{c.i}</div>
              <div className="font-display text-xl mv-green-text">{c.t}</div>
              <p className="text-[14px] mt-2" style={{ color: "var(--mv-muted)" }}>{c.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 mv-card p-5 flex items-start gap-3" style={{ background: "linear-gradient(135deg, rgba(10,59,40,0.05), rgba(212,175,55,0.12))" }} data-testid="manifesto-plain-note">
          <FileText size={20} className="mv-green-text shrink-0" style={{ color: "var(--mv-gold)" }}/>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--mv-gold)" }}>📜 Plain-language Manifesto</div>
            <p className="mt-1 text-[14px] mv-green-text font-semibold">
              No legal fine print. Every promise on this page is written the way you'd say it out loud —
              in plain English (and 8 other languages). We refuse to hide meaning behind small text.
            </p>
          </div>
        </div>
      </Section>

      {/* WE WILL NEVER */}
      <Section eyebrow="§ Non-Negotiable Rules" title="We will" italic="never.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {NEVER_RULES.map((r) => (
            <div key={r} className="mv-card p-6 flex gap-4 items-start" style={{ background: "linear-gradient(180deg, #fff, rgba(233,60,60,0.04))" }}>
              <XCircle size={26} className="shrink-0" style={{ color: "#B33A3A" }}/>
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em]" style={{ color: "#B33A3A" }}>Never</div>
                <div className="font-display text-lg mv-green-text mt-1">{r}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 text-[13.5px] font-accent italic" style={{ color: "var(--mv-muted)" }}>
          These four rules are hard-coded into every MAVULA contract. Any change requires your written consent.
        </div>
      </Section>

      <ComparisonCard />

      {/* INDEPENDENT ACCOUNTABILITY */}
      <Section eyebrow="§ Independent Accountability" title="Third-party guardians —" italic="not our own staff.">
        {/* TITLE GUARDIANS */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-2xl flex items-center justify-center mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>
              <Scale size={18}/>
            </div>
            <h3 className="font-display text-2xl mv-green-text">Independent Title Guardians</h3>
          </div>
          <p className="text-[14.5px]" style={{ color: "var(--mv-muted)" }}>
            A public list of qualified lawyers and land officers verifying every title. This directory is being
            vetted — entries appear only after independent verification.
          </p>
          <div className="mt-5">
            {loading ? (
              <div className="mv-card p-6 text-neutral-500">Loading directory…</div>
            ) : guardians.length === 0 ? (
              <div className="mv-card p-6 flex items-start gap-3" style={{ background: "linear-gradient(180deg, #fff, rgba(212,175,55,0.06))" }}>
                <ShieldCheck className="mv-green-text shrink-0" size={22}/>
                <div>
                  <div className="font-semibold mv-green-text">Directory being verified.</div>
                  <div className="text-[14px] mt-1" style={{ color: "var(--mv-muted)" }}>
                    Entries will only be published after independent vetting. If you are a qualified land-law
                    professional and want to be listed, contact us via WhatsApp +44 7424 311882.
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="guardians-list">
                {guardians.map((g) => (
                  <div key={g.id} className="mv-card p-5">
                    <div className="font-display text-lg mv-green-text">{g.name}</div>
                    <div className="text-[13.5px]" style={{ color: "var(--mv-muted)" }}>{g.firm} · {g.country}</div>
                    {g.notes && <div className="mt-2 text-[13px]">{g.notes}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* REVIEW PANEL */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-2xl flex items-center justify-center mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>
              <Users size={18}/>
            </div>
            <h3 className="font-display text-2xl mv-green-text">Independent Review Panel</h3>
          </div>
          <p className="text-[14.5px]" style={{ color: "var(--mv-muted)" }}>
            All grievances are reviewed by a neutral third-party panel — never only by internal staff.
            Contact WhatsApp +44 7424 311882 to raise a formal review.
          </p>
          <div className="mt-5">
            {panel.length === 0 ? (
              <div className="mv-card p-6 flex items-start gap-3" style={{ background: "linear-gradient(180deg, #fff, rgba(212,175,55,0.06))" }}>
                <Users className="mv-green-text shrink-0" size={22}/>
                <div>
                  <div className="font-semibold mv-green-text">Panel being constituted.</div>
                  <div className="text-[14px] mt-1" style={{ color: "var(--mv-muted)" }}>
                    Panel members are being independently vetted. All names will appear here publicly once confirmed.
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {panel.map((g) => (
                  <div key={g.id} className="mv-card p-5">
                    <div className="font-display text-lg mv-green-text">{g.name}</div>
                    <div className="text-[13.5px]" style={{ color: "var(--mv-muted)" }}>{g.firm} · {g.country}</div>
                    {g.notes && <div className="mt-2 text-[13px]">{g.notes}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* FREE LEGAL GUIDE */}
      <Section eyebrow="§ Free Resource" title="How to read your title deed —" italic="in plain language.">
        <div className="mv-card p-8 flex flex-col md:flex-row items-start gap-6" style={{ background: "linear-gradient(135deg, rgba(10,59,40,0.05), rgba(212,175,55,0.10))" }}>
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mv-green-text shrink-0" style={{ background: "rgba(212,175,55,0.16)" }}>
            <FileText size={28}/>
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--mv-gold)" }}>Free · No sign-up</div>
            <h3 className="font-display text-2xl mv-green-text mt-1">MAVULA Title Deed Reader — Plain Language Guide</h3>
            <p className="mt-2 text-[14.5px]" style={{ color: "var(--mv-muted)" }}>
              A short PDF explaining exactly what to look for on any title deed — how to check the owner name,
              boundaries, encumbrances, restrictions, and where every clause sits.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a href="/downloads/MAVULA-Title-Deed-Guide.pdf" download className="btn-gold inline-flex items-center gap-2" data-testid="download-title-guide">
                <Download size={16}/> Download PDF Guide
                <span className="text-[9.5px] font-bold uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full ml-1" style={{ background: "rgba(212,60,60,0.18)", color: "#8a2020" }}>SAMPLE</span>
              </a>
              <a href="https://wa.me/447424311882?text=I%20have%20a%20question%20about%20my%20title%20deed" target="_blank" rel="noopener" className="btn-ghost">
                Ask on WhatsApp
              </a>
            </div>
            <div className="mt-4 text-[12px] font-accent italic" style={{ color: "var(--mv-muted)" }}>
              This PDF is a <strong style={{ color: "#8a2020" }}>SAMPLE</strong> — educational only, watermarked on every page, and not legal advice. Always confirm with a qualified lawyer in the country of purchase.
            </div>
          </div>
        </div>
      </Section>
    </main>
  );
}
